import React, { useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, Legend,
} from 'recharts';
import { ShieldCheck, FileText, TrendingUp, Info, ChevronDown, Layers, Download, ExternalLink, FileCheck2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

// ── formatters ───────────────────────────────────────────
// All P&L / balance-sheet values from the API are already in INR crore.
const crore = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L Cr`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })} Cr`;
};
const rupeesCrore = (v) => {
  // raw rupees (market cap) -> crore display
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v) / 1e7;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L Cr`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })} Cr`;
};
const num = (v, d = 2) =>
  v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: d });
const pct = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v).toFixed(2)}%`);
const fyLabel = (iso) => {
  if (!iso) return '—';
  const [y, m] = iso.split('-');
  // Indian fiscal year ends Mar -> FY of the ending year
  return m === '03' ? `FY${y.slice(2)}` : iso;
};
const qLabel = (iso) => {
  if (!iso) return '—';
  const [y, m] = iso.split('-');
  const q = { '06': 'Q1', '09': 'Q2', '12': 'Q3', '03': 'Q4' }[m] || iso;
  return `${q} ${y.slice(2)}`;
};
const margin = (numer, denom) =>
  numer == null || !denom ? null : (Number(numer) / Number(denom)) * 100;
const fmtRatio = (unit, v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (unit === '%') return `${n.toFixed(2)}%`;
  if (unit === 'x') return `${n.toFixed(2)}x`;
  if (unit === 'days') return `${n.toFixed(0)}d`;
  return num(n);
};

// ── small UI bits ────────────────────────────────────────
function StatTile({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold mt-0.5">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function VerifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-500">
      <ShieldCheck className="w-3.5 h-3.5" /> Exchange-verified
    </span>
  );
}

// ── trend chart (annual revenue + PAT bars, margin line) ─
function TrendChart({ annual }) {
  const data = [...annual]
    .filter((r) => r.revenue != null)
    .reverse()
    .map((r) => ({
      period: fyLabel(r.period_end),
      Revenue: r.revenue,
      PAT: r.pat,
      'Net margin %': margin(r.pat, r.revenue),
    }));
  if (data.length < 2) return null;
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-emerald-500" />
          <p className="text-sm font-semibold">Revenue, profit &amp; margin trend</p>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v.toLocaleString('en-IN'))} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v?.toFixed?.(0)}%`} />
            <RechartsTooltip
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
              formatter={(v, n) => (n === 'Net margin %' ? [`${Number(v).toFixed(1)}%`, n] : [crore(v), n])}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="l" dataKey="Revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} maxBarSize={36} />
            <Bar yAxisId="l" dataKey="PAT" fill="hsl(var(--primary) / 0.45)" radius={[4, 4, 0, 0]} maxBarSize={36} />
            <Line yAxisId="r" type="monotone" dataKey="Net margin %" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ── statement table ──────────────────────────────────────
function StatementTable({ rows, label, periodFmt, onPick }) {
  if (!rows || rows.length === 0) return null;
  const lines = [
    { key: 'revenue', name: 'Revenue', fmt: crore },
    { key: 'ebitda', name: 'EBITDA', fmt: crore },
    { key: '_ebitdam', name: 'EBITDA margin', fmt: pct, derive: (r) => margin(r.ebitda, r.revenue) },
    { key: 'pat', name: 'Net profit (PAT)', fmt: crore, strong: true },
    { key: '_npm', name: 'Net margin', fmt: pct, derive: (r) => margin(r.pat, r.revenue) },
    { key: 'eps', name: 'EPS (₹)', fmt: (v) => (v == null ? '—' : `₹${num(v)}`) },
    { key: 'debt', name: 'Total borrowings', fmt: crore },
    { key: 'cash', name: 'Cash & equivalents', fmt: crore },
    { key: '_netdebt', name: 'Net debt', fmt: crore, derive: (r) => (r.debt == null && r.cash == null ? null : (Number(r.debt || 0) - Number(r.cash || 0))) },
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-border/60">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/40">
            <th className="text-left py-2.5 px-4 font-medium text-muted-foreground sticky left-0 bg-muted/40 min-w-[170px]">
              {label}
            </th>
            {rows.map((r) => (
              <th key={r.period_end} className="text-right py-2.5 px-3 font-semibold whitespace-nowrap">
                {onPick ? (
                  <button type="button" onClick={() => onPick(r)} className="inline-flex items-center gap-1 hover:text-emerald-500 hover:underline">
                    {periodFmt(r.period_end)}
                    {r.verification_status === 'VERIFIED' && <ShieldCheck className="w-3 h-3 text-emerald-500" />}
                  </button>
                ) : (
                  <>
                    {periodFmt(r.period_end)}
                    {r.verification_status === 'VERIFIED' && (
                      <ShieldCheck className="inline w-3 h-3 ml-1 text-emerald-500 align-text-top" />
                    )}
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((ln) => (
            <tr key={ln.key} className="border-t border-border/40 hover:bg-muted/20">
              <td className={`py-2 px-4 sticky left-0 bg-background ${ln.strong ? 'font-semibold' : ''}`}>{ln.name}</td>
              {rows.map((r) => {
                const val = ln.derive ? ln.derive(r) : r[ln.key];
                return (
                  <td key={r.period_end} className={`text-right py-2 px-3 whitespace-nowrap ${ln.strong ? 'font-semibold' : ''}`}>
                    {ln.fmt(val)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── balance sheet ────────────────────────────────────────
const BS_LINES = [
  { section: 'Assets' },
  { key: 'total_assets', name: 'Total assets', strong: true },
  { key: 'noncurrent_assets', name: 'Non-current assets' },
  { key: 'ppe', name: 'Property, plant & equipment' },
  { key: 'cwip', name: 'Capital work-in-progress' },
  { key: 'goodwill', name: 'Goodwill' },
  { key: 'intangible_assets', name: 'Intangible assets' },
  { key: 'noncurrent_investments', name: 'Non-current investments' },
  { key: 'current_assets', name: 'Current assets' },
  { key: 'inventories', name: 'Inventories' },
  { key: 'trade_receivables', name: 'Trade receivables' },
  { key: 'current_investments', name: 'Current investments' },
  { key: 'cash', name: 'Cash & bank balances' },
  { section: 'Equity' },
  { key: 'equity_attributable_owners', name: 'Net worth (owners)', strong: true },
  { key: 'share_capital', name: 'Share capital' },
  { key: 'other_equity', name: 'Other equity / reserves' },
  { key: 'non_controlling_interest', name: 'Minority interest' },
  { section: 'Liabilities' },
  { key: 'total_liabilities', name: 'Total liabilities', strong: true },
  { key: 'borrowings_noncurrent', name: 'Borrowings — non-current' },
  { key: 'borrowings_current', name: 'Borrowings — current' },
  { key: 'trade_payables', name: 'Trade payables' },
  { key: 'noncurrent_liabilities', name: 'Non-current liabilities' },
  { key: 'current_liabilities', name: 'Current liabilities' },
];

function BalanceSheetTable({ rows, onPick }) {
  if (!rows || rows.length === 0) return null;
  const hasData = (key) => rows.some((r) => r[key] != null);
  return (
    <div className="overflow-x-auto rounded-xl border border-border/60">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/40">
            <th className="text-left py-2.5 px-4 font-medium text-muted-foreground sticky left-0 bg-muted/40 min-w-[200px]">
              Balance sheet (₹ Cr)
            </th>
            {rows.map((r) => (
              <th key={r.period_end} className="text-right py-2.5 px-3 font-semibold whitespace-nowrap">
                {onPick ? (
                  <button type="button" onClick={() => onPick(r)} className="inline-flex items-center gap-1 hover:text-emerald-500 hover:underline">
                    {fyLabel(r.period_end)}
                    {r.verification_status === 'VERIFIED' && <ShieldCheck className="w-3 h-3 text-emerald-500" />}
                  </button>
                ) : (
                  <>
                    {fyLabel(r.period_end)}
                    {r.verification_status === 'VERIFIED' && (
                      <ShieldCheck className="inline w-3 h-3 ml-1 text-emerald-500 align-text-top" />
                    )}
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {BS_LINES.map((ln, i) => {
            if (ln.section) {
              return (
                <tr key={`s-${ln.section}`} className="border-t border-border/40 bg-muted/20">
                  <td className="py-1.5 px-4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sticky left-0 bg-muted/20" colSpan={rows.length + 1}>
                    {ln.section}
                  </td>
                </tr>
              );
            }
            if (!hasData(ln.key)) return null;
            return (
              <tr key={ln.key} className="border-t border-border/40 hover:bg-muted/20">
                <td className={`py-2 px-4 sticky left-0 bg-background ${ln.strong ? 'font-semibold' : ''}`}>{ln.name}</td>
                {rows.map((r) => (
                  <td key={r.period_end} className={`text-right py-2 px-3 whitespace-nowrap ${ln.strong ? 'font-semibold' : ''}`}>
                    {crore(r[ln.key])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── ratio families ───────────────────────────────────────
function RatioTable({ family }) {
  const { periods, rows } = family;
  return (
    <div className="overflow-x-auto rounded-lg border border-border/50">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/30">
            <th className="text-left py-2 px-4 font-medium text-muted-foreground sticky left-0 bg-muted/30 min-w-[200px]">Ratio</th>
            {periods.map((p) => (
              <th key={p} className="text-right py-2 px-3 font-semibold whitespace-nowrap">{fyLabel(p)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-t border-border/40 hover:bg-muted/20">
              <td className="py-2 px-4 sticky left-0 bg-background">
                {r.label} <span className="text-muted-foreground">({r.unit})</span>
              </td>
              {r.values.map((v, i) => (
                <td key={i} className="text-right py-2 px-3 whitespace-nowrap tabular-nums">{fmtRatio(r.unit, v)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RatioFamilies({ families }) {
  const [open, setOpen] = useState(() => new Set((families || []).map((f) => f.key)));
  if (!families || families.length === 0) return null;
  const toggle = (k) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-4 h-4 text-emerald-500" />
          <p className="text-sm font-semibold">Financial ratios</p>
          <span className="text-[11px] text-muted-foreground">derived from filed statements</span>
        </div>
        {families.map((f) => {
          const isOpen = open.has(f.key);
          return (
            <div key={f.key} className="rounded-xl border border-border/50 overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(f.key)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/20"
              >
                <span className="text-sm font-medium">{f.title}</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && <div className="px-4 pb-4">{<RatioTable family={f} />}</div>}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ── filing viewer (renders the filed XBRL nicely, not raw XML) ─
function KV({ rows, title }) {
  const shown = rows.filter((r) => r.value != null && r.value !== '—');
  if (shown.length === 0) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{title}</p>
      <div className="rounded-lg border border-border/60 divide-y divide-border/40">
        {shown.map((r) => (
          <div key={r.label} className="flex items-center justify-between px-3 py-1.5 text-sm">
            <span className={r.strong ? 'font-semibold' : 'text-muted-foreground'}>{r.label}</span>
            <span className={`tabular-nums ${r.strong ? 'font-semibold' : ''}`}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilingModal({ filing, companyName, onClose }) {
  const open = !!filing;
  const pl = filing?.pl || {};
  const bs = filing?.bs || {};
  const periodLabel = filing?.kind === 'Q' ? qLabel(pl.period_end) : `FY${(pl.period_end || '').slice(2, 4)}`;
  const fmtC = (v) => crore(v);
  const plRows = [
    { label: 'Revenue', value: fmtC(pl.revenue), strong: true },
    { label: 'Other income', value: fmtC(pl.other_income) },
    { label: 'EBITDA', value: fmtC(pl.ebitda) },
    { label: 'EBITDA margin', value: pl.revenue ? pct(margin(pl.ebitda, pl.revenue)) : null },
    { label: 'Finance cost', value: fmtC(pl.finance_cost) },
    { label: 'Depreciation & amortisation', value: fmtC(pl.depreciation) },
    { label: 'Profit before tax', value: fmtC(pl.pbt) },
    { label: 'Net profit (PAT)', value: fmtC(pl.pat), strong: true },
    { label: 'Net margin', value: pl.revenue ? pct(margin(pl.pat, pl.revenue)) : null },
    { label: 'EPS (₹)', value: pl.eps != null ? `₹${num(pl.eps)}` : null },
    { label: 'Net interest income (banks)', value: fmtC(pl.nii) },
  ];
  const bsRows = [
    { label: 'Total assets', value: fmtC(bs.total_assets), strong: true },
    { label: 'Property, plant & equipment', value: fmtC(bs.ppe) },
    { label: 'Current assets', value: fmtC(bs.current_assets) },
    { label: 'Inventories', value: fmtC(bs.inventories) },
    { label: 'Trade receivables', value: fmtC(bs.trade_receivables) },
    { label: 'Cash & bank', value: fmtC(bs.cash) },
    { label: 'Net worth (owners)', value: fmtC(bs.equity_attributable_owners), strong: true },
    { label: 'Share capital', value: fmtC(bs.share_capital) },
    { label: 'Other equity / reserves', value: fmtC(bs.other_equity) },
    { label: 'Total borrowings', value: fmtC((Number(bs.borrowings_noncurrent || 0) + Number(bs.borrowings_current || 0)) || pl.debt) },
    { label: 'Trade payables', value: fmtC(bs.trade_payables) },
    { label: 'Current liabilities', value: fmtC(bs.current_liabilities) },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck2 className="w-4.5 h-4.5 text-emerald-500" />
            {companyName} — {periodLabel} results
          </DialogTitle>
          <DialogDescription>
            Filed with NSE
            {pl.filing_date ? ` on ${new Date(pl.filing_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
            {pl.basis ? ` · ${pl.basis.charAt(0) + pl.basis.slice(1).toLowerCase()}` : ''}
            {pl.audited != null ? ` · ${pl.audited ? 'Audited' : 'Unaudited'}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <VerifiedBadge />
          {pl.verification_status === 'VERIFIED' && (
            <span className="text-[11px] text-muted-foreground">XBRL cross-verified</span>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-1">
          <KV title={filing?.kind === 'Q' ? 'Quarterly P&L' : 'Annual P&L'} rows={plRows} />
          {filing?.kind !== 'Q' && <KV title="Balance sheet" rows={bsRows} />}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/40 mt-1">
          {pl.xbrl_url && (
            <a href={pl.xbrl_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-500 hover:underline">
              <Download className="w-3.5 h-3.5" /> Download original XBRL
            </a>
          )}
          {pl.source_url && pl.source_url !== pl.xbrl_url && (
            <a href={pl.source_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground hover:underline">
              <ExternalLink className="w-3.5 h-3.5" /> Filing on NSE
            </a>
          )}
          <span className="text-[11px] text-muted-foreground ml-auto">All figures ₹ crore, as filed.</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── main ─────────────────────────────────────────────────
export default function VerifiedFinancials({ data, companyName = 'This company' }) {
  const {
    annual = [], quarterly = [], balance_sheet = [], ratio_families = [],
    valuation, headline, ratios, snapshot, provenance,
  } = data || {};
  const latest = annual[0] || quarterly[0] || {};
  const h = headline || {};
  const val = valuation || {};

  const [filing, setFiling] = useState(null);
  const bsByPeriod = React.useMemo(
    () => Object.fromEntries(balance_sheet.map((b) => [b.period_end, b])),
    [balance_sheet],
  );
  const annualByPeriod = React.useMemo(
    () => Object.fromEntries(annual.map((a) => [a.period_end, a])),
    [annual],
  );
  const openAnnual = (row) => setFiling({ kind: 'FY', pl: row, bs: bsByPeriod[row.period_end] });
  const openQuarter = (row) => setFiling({ kind: 'Q', pl: row, bs: null });
  const openFromBs = (row) => setFiling({ kind: 'FY', pl: annualByPeriod[row.period_end] || { period_end: row.period_end, xbrl_url: row.xbrl_url, source_url: row.source_url }, bs: row });

  return (
    <div className="space-y-5">
      {/* provenance banner */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] px-4 py-3">
        <div className="flex items-center gap-3">
          <VerifiedBadge />
          <p className="text-xs text-muted-foreground">
            {provenance?.source || 'NSE exchange filings (XBRL)'} ·{' '}
            {provenance?.verified_periods ?? provenance?.periods ?? annual.length} periods cross-checked ·
            audited & filed numbers
          </p>
        </div>
        {annual[0] && (
          <button
            type="button"
            onClick={() => openAnnual(annual[0])}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-500 hover:bg-emerald-500/20 shrink-0"
          >
            <FileText className="w-3.5 h-3.5" /> View latest filing
          </button>
        )}
      </div>

      {/* headline ratios */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <StatTile label="Market cap" value={crore(val.market_cap)} sub={val.price != null ? `₹${num(val.price)} / share` : null} />
        <StatTile label="P/E" value={num(val.pe ?? ratios?.pe)} />
        <StatTile label="P/B" value={num(val.pb ?? ratios?.pb)} />
        <StatTile label="ROE" value={pct(h.roe ?? ratios?.roe)} />
        <StatTile label="ROCE" value={pct(h.roce)} />
        <StatTile label="ROA" value={pct(h.roa)} />
        <StatTile label="Net margin" value={pct(h.net_margin ?? ratios?.npm)} />
        <StatTile label="Debt / Equity" value={h.debt_equity != null ? `${num(h.debt_equity)}x` : '—'} />
      </div>

      {/* latest year snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <StatTile label={`Revenue (${fyLabel(latest.period_end)})`} value={crore(latest.revenue)} />
        <StatTile label="Net profit" value={crore(latest.pat)} />
        <StatTile label="EPS" value={latest.eps != null ? `₹${num(latest.eps)}` : '—'} />
        <StatTile label="EV / EBITDA" value={val.ev_ebitda != null ? `${num(val.ev_ebitda)}x` : '—'} />
        <StatTile label="Div. yield" value={pct(val.dividend_yield)} />
        <StatTile label="Rev. CAGR (3y)" value={pct(h.revenue_cagr_3y)} />
      </div>

      <TrendChart annual={annual} />

      {/* ratio families (bull-ai style, per-year) */}
      <RatioFamilies families={ratio_families} />

      {/* balance sheet */}
      {balance_sheet.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-semibold mb-3">Balance sheet</p>
            <BalanceSheetTable rows={balance_sheet} onPick={openFromBs} />
          </CardContent>
        </Card>
      )}

      {/* annual statement */}
      {annual.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">Annual results</p>
              <span className="text-[11px] text-muted-foreground">tap a year to view its filing</span>
            </div>
            <StatementTable rows={annual} label="Annual (consolidated)" periodFmt={fyLabel} onPick={openAnnual} />
          </CardContent>
        </Card>
      )}

      {/* quarterly statement */}
      {quarterly.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">Quarterly results</p>
              <span className="text-[11px] text-muted-foreground">tap a quarter to view its filing</span>
            </div>
            <StatementTable rows={quarterly} label="Quarter (consolidated)" periodFmt={qLabel} onPick={openQuarter} />
          </CardContent>
        </Card>
      )}

      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Info className="w-3 h-3" />
        Figures sourced directly from exchange filings and cross-verified against XBRL — the same audited data the KubAIr committee reasons over. Balance-sheet &amp; balance-sheet-derived ratios available from FY2023 onward; a few line items banks/insurers don&apos;t file in XBRL show as &ldquo;—&rdquo;.
      </p>

      <FilingModal filing={filing} companyName={companyName} onClose={() => setFiling(null)} />
    </div>
  );
}

export { crore, rupeesCrore, num, pct };
